import { useToast } from "@au-schein-de/au-ui-library";
import * as Sentry from "@sentry/react-router";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useLocation, useRevalidator } from "react-router";
import { useLocalStorage } from "~/hooks/use-local-storage.hook";
import { useSyncFavorites } from "~/hooks/use-sync-favorites.hook";

export type FavoriteItem = {
	productId: number;
	createdAt: string; // ISO string
};

type FavoritesContextType = {
	addFavorite: (productId: number, productName?: string) => void;
	removeFavorite: (productId: number, productName?: string) => void;
	undoRemoveFavorite: () => void;
	isFavorite: (productId: number | string) => boolean;
	toggleFavorite: (productId: number | string, productName?: string) => void;
	isLoading: boolean;
	isAuthenticated: boolean;
	favoriteIds: number[];
	favoriteItems: FavoriteItem[];
};

const FavoritesContext = createContext<FavoritesContextType | undefined>(
	undefined,
);

export const FAVORITES_KEY = "da-marketplace:favorites";
const EMPTY_ARRAY: FavoriteItem[] = [];

type FavoritesProviderProps = PropsWithChildren<{
	user: unknown; // We just need to know if it exists
	shouldSyncFavoritesAfterLogin?: boolean;
}>;

export function FavoritesProvider({
	children,
	user,
	shouldSyncFavoritesAfterLogin = false,
}: FavoritesProviderProps) {
	const { t } = useTranslation("product");
	const { t: tCommon } = useTranslation("common");
	const { toast } = useToast();
	const toastPosition = "bottom-right";
	const addFetcher = useFetcher();
	const removeFetcher = useFetcher();
	const revalidator = useRevalidator();

	const previousAddStateRef = useRef<FavoriteItem[]>([]);
	const previousRemoveStateRef = useRef<FavoriteItem[]>([]);
	const lastRemovedItemRef = useRef<{
		item: FavoriteItem;
		productName?: string;
	} | null>(null);

	const options = useMemo(() => ({ initializeWithValue: true }), []);
	const [rawFavoriteItems, setRawFavoriteItems] = useLocalStorage<
		FavoriteItem[]
	>(FAVORITES_KEY, EMPTY_ARRAY, options);

	const [isClient, setIsClient] = useState(false);
	const location = useLocation();

	useEffect(() => {
		setIsClient(true);
	}, []);

	const isAuthenticated = !!user;

	const favoriteItems = isClient ? rawFavoriteItems : EMPTY_ARRAY;

	// Sort by createdAt descending (newest first)
	const sortedFavoriteItems = useMemo(
		() =>
			[...favoriteItems].sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			),
		[favoriteItems],
	);

	// Computed property for backward compatibility
	const favoriteIds = useMemo(
		() => sortedFavoriteItems.map((item) => item.productId),
		[sortedFavoriteItems],
	);

	const hasPendingOperations =
		addFetcher.state !== "idle" || removeFetcher.state !== "idle";

	useSyncFavorites({
		isAuthenticated,
		favoriteItems,
		setFavoriteItems: setRawFavoriteItems,
		isClient,
		hasPendingOperations,
		shouldSyncFavoritesAfterLogin,
		shouldPullServerFavorites:
			isAuthenticated && location.pathname === "/favorites",
	});

	const isLoading = hasPendingOperations || revalidator.state === "loading";

	const addFavorite = useCallback(
		(productId: number, productName?: string) => {
			setRawFavoriteItems((prev) => {
				previousAddStateRef.current = prev;

				const existing = prev.find((item) => item.productId === productId);
				if (existing) return prev;

				const newItem: FavoriteItem = {
					productId,
					createdAt: new Date().toISOString(),
				};

				return [...prev, newItem];
			});

			if (isAuthenticated) {
				addFetcher.submit(
					{ productId: productId.toString() },
					{ method: "POST", action: "/api/favorites/add" },
				);
			}

			if (productName) {
				toast({
					title: (
						<div className="flex flex-col gap-2">
							<div className="flex flex-col">
								<span className="text-body text-gray-300">
									{t("favorites.added")}
								</span>
								<span className="text-body text-gray-300">{productName}</span>
							</div>
							<a
								href="/favorites"
								className="text-ansay-main text-body-bold pt-3 pb-4 cursor-pointer"
							>
								{t("favorites.show")}
							</a>
						</div>
					),
					variant: "favorites",
					position: toastPosition,
				});
			}
		},
		[isAuthenticated, addFetcher, setRawFavoriteItems, toast, t],
	);

	const undoRemoveFavorite = useCallback(() => {
		if (!lastRemovedItemRef.current) return;

		const { item, productName } = lastRemovedItemRef.current;
		lastRemovedItemRef.current = null;

		addFavorite(item.productId, productName);
	}, [addFavorite]);

	const removeFavorite = useCallback(
		(productId: number, productName?: string) => {
			setRawFavoriteItems((prev) => {
				previousRemoveStateRef.current = prev;

				const removedItem = prev.find((item) => item.productId === productId);
				if (removedItem) {
					lastRemovedItemRef.current = { item: removedItem, productName };
				}

				return prev.filter((item) => item.productId !== productId);
			});

			if (isAuthenticated) {
				removeFetcher.submit(
					{ productId: productId.toString() },
					{ method: "DELETE", action: "/api/favorites/remove" },
				);
			}

			if (productName) {
				toast({
					title: (
						<div className="flex flex-col gap-2">
							<div className="flex flex-col">
								<span className="text-body text-gray-300">
									{t("favorites.removed")}
								</span>
								<span className="text-body text-gray-300">{productName}</span>
							</div>
							<button
								type="button"
								onClick={undoRemoveFavorite}
								className="text-ansay-main text-body-bold text-left pt-3 pb-4 cursor-pointer"
								data-testid="toast-undo-button"
							>
								{tCommon("undo")}
							</button>
						</div>
					),
					variant: "favorites",
					position: toastPosition,
				});
			}
		},
		[
			isAuthenticated,
			removeFetcher,
			setRawFavoriteItems,
			toast,
			t,
			tCommon,
			undoRemoveFavorite,
		],
	);

	const isFavorite = useCallback(
		(productId: number | string): boolean => {
			const productIdNum =
				typeof productId === "string" ? Number(productId) : productId;
			return favoriteIds.includes(productIdNum);
		},
		[favoriteIds],
	);

	const toggleFavorite = useCallback(
		(productId: number | string, productName?: string) => {
			const productIdNum =
				typeof productId === "string" ? Number(productId) : productId;
			const isCurrentlyFavorite = favoriteIds.includes(productIdNum);

			if (isCurrentlyFavorite) {
				removeFavorite(productIdNum, productName);
			} else {
				addFavorite(productIdNum, productName);
			}
		},
		[favoriteIds, removeFavorite, addFavorite],
	);

	// Handle addFetcher response (errors only)
	useEffect(() => {
		if (addFetcher.state === "idle" && addFetcher.data?.error) {
			// Revert optimistic update
			setRawFavoriteItems(previousAddStateRef.current);

			// The session was refused (the account is gone): no report and no toast.
			if (addFetcher.data.error.code === "UNAUTHORIZED") {
				return;
			}
			Sentry.captureMessage("Failed to add favorite", {
				level: "warning",
				extra: {
					error: addFetcher.data.error,
					context: "favorites-add",
				},
			});

			// Show error toast
			toast({
				title:
					typeof addFetcher.data.error === "string"
						? addFetcher.data.error
						: t("favorites.addError"),
				variant: "destructive",
			});
		}
	}, [addFetcher.data, addFetcher.state, setRawFavoriteItems, toast, t]);

	// Handle removeFetcher response (errors only)
	useEffect(() => {
		if (removeFetcher.state === "idle" && removeFetcher.data?.error) {
			// Revert optimistic update
			setRawFavoriteItems(previousRemoveStateRef.current);

			if (removeFetcher.data.error.code === "UNAUTHORIZED") {
				return;
			}
			Sentry.captureMessage("Failed to remove favorite", {
				level: "warning",
				extra: {
					error: removeFetcher.data.error,
					context: "favorites-remove",
				},
			});

			// Show error toast
			toast({
				title:
					typeof removeFetcher.data.error === "string"
						? removeFetcher.data.error
						: t("favorites.removeError"),
				variant: "destructive",
			});
		}
	}, [removeFetcher.data, removeFetcher.state, setRawFavoriteItems, toast, t]);

	const value = useMemo(
		() => ({
			addFavorite,
			removeFavorite,
			undoRemoveFavorite,
			isFavorite,
			toggleFavorite,
			isLoading,
			isAuthenticated,
			favoriteIds,
			favoriteItems: sortedFavoriteItems,
		}),
		[
			addFavorite,
			removeFavorite,
			undoRemoveFavorite,
			isFavorite,
			toggleFavorite,
			isLoading,
			isAuthenticated,
			favoriteIds,
			sortedFavoriteItems,
		],
	);

	return (
		<FavoritesContext.Provider value={value}>
			{children}
		</FavoritesContext.Provider>
	);
}

export function useFavoritesContext() {
	const context = useContext(FavoritesContext);
	if (context === undefined) {
		throw new Error(
			"useFavoritesContext must be used within a FavoritesProvider",
		);
	}
	return context;
}
